import {useMemo} from "react";

import {useGetHasPermissions, useStoredMe} from "@/src/modules/core/store/me";
import {useStoredUserProperty} from "@/src/modules/core/store/user-property";
import type {Me} from "@/src/modules/core/types";

import {MAIN_ROUTES, MANAGEMENT_ROUTES} from "../routes/authenticated";
import {
  isResidentBoardRouteKey,
  RESIDENT_BOARD_MANAGEMENT_ROUTE_KEYS,
  RESIDENT_BOARD_MAIN_ROUTE_KEYS,
} from "../lib/resident-board-nav-route-keys";
import type {Route} from "../types";

function sortRoutes(a: Route, b: Route) {
  return a.position && b.position ? a.position - b.position : 1;
}

function sortManagementRoutes(a: Route, b: Route) {
  return (a.label ?? "").localeCompare(b.label ?? "", undefined, {sensitivity: "base"});
}

function filterMainBoardRoutes(
  routes: Route[],
  me: Me | null | undefined,
  getHasPermission: (permission: string) => boolean,
  userProperty: ReturnType<typeof useStoredUserProperty>,
): Route[] {
  const mainKeySet = new Set<string>(RESIDENT_BOARD_MAIN_ROUTE_KEYS);

  if (userProperty?.service_tier === "free_inspection") {
    return routes.filter(
      (route) =>
        route.key === "propertyinspectionreport" &&
        mainKeySet.has(route.key) &&
        getHasPermission("propertyinspectionreport show"),
    );
  }

  return routes
    .filter((route) => route.key && mainKeySet.has(route.key))
    .filter((route) => {
      if (!userProperty) return false;

      const section = userProperty.sections.find((section) => section.key === route.key);
      const isSectionGranted = !!section && section.granted;

      if (route.withPermissionGuard) {
        return isSectionGranted && getHasPermission(`${route.permission} show`);
      }

      return isSectionGranted;
    });
}

function filterManagementBoardRoutes(
  routes: Route[],
  me: Me | null | undefined,
  getHasPermission: (permission: string) => boolean,
  userProperty: ReturnType<typeof useStoredUserProperty>,
): Route[] {
  const managementKeySet = new Set<string>(RESIDENT_BOARD_MANAGEMENT_ROUTE_KEYS);
  const isFreeInspectionRestricted =
    userProperty?.service_tier === "free_inspection" && !me?.isAdmin && !me?.isSeniorSupervisor;

  if (isFreeInspectionRestricted) {
    return [];
  }

  const staticRoutes = routes
    .filter((route) => route.static && route.key && managementKeySet.has(route.key))
    .filter((route) => {
      if (route.key === "reportrequests" && !me?.properties?.length) {
        return false;
      }
      if (route.withPermissionGuard) {
        return getHasPermission(`${route.permission} show`);
      }
      return true;
    });

  if (!userProperty) {
    return staticRoutes;
  }

  const dynamicRoutes = routes
    .filter((route) => !route.static && route.key && managementKeySet.has(route.key))
    .filter((route) => {
      const section = userProperty.sections.find((section) => section.key === route.key);
      const isSectionGranted = !!section && section.granted;

      if (route.withPermissionGuard) {
        return isSectionGranted && getHasPermission(`${route.permission} show`);
      }

      return isSectionGranted;
    });

  return [...staticRoutes, ...dynamicRoutes];
}

export default function useResidentBoardNavRoutes() {
  const getHasPermission = useGetHasPermissions();
  const userProperty = useStoredUserProperty();
  const me = useStoredMe();

  return useMemo(() => {
    const homeRoute = MAIN_ROUTES.find((route) => route.key === "home");
    const mainRoutes = filterMainBoardRoutes(MAIN_ROUTES, me, getHasPermission, userProperty).sort(
      sortRoutes,
    );
    const managementRoutes = filterManagementBoardRoutes(
      MANAGEMENT_ROUTES,
      me,
      getHasPermission,
      userProperty,
    ).sort(sortManagementRoutes);

    const moduleRoutes = [...mainRoutes, ...managementRoutes].filter((route) =>
      isResidentBoardRouteKey(route.key),
    );

    return homeRoute ? [homeRoute, ...moduleRoutes] : moduleRoutes;
  }, [getHasPermission, me, userProperty]);
}
